import { useCallback, useEffect, useState } from "react";
import { fetchTvtCurrent, fetchTvtHistory } from "../services/api.js";

/**
 * Treasury Value per Token — sourced from the backend's `/api/tvt/current`
 * endpoint (which reads the TVT contract server-side). No direct chain
 * access from the frontend, per the data-flow spec.
 */
export function useTvt({ pollMs = 60_000 } = {}) {
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // `opts.background === true` = silent poll refresh: keep the current
    // TVT figure and chart on screen while re-fetching so nothing flips
    // back to shimmer on the 60s poll. Only foreground calls toggle
    // loading / null the value on error.
    const refresh = useCallback(async (opts = {}) => {
        const background = opts.background === true;
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const live = await fetchTvtCurrent();
            // `tvtValid` is the source-of-truth for "is the TVT figure
            // meaningful right now?" — the backend sets it to false when
            // the on-chain getter reverts (e.g. circulating supply is
            // still 0 so the divide-by-zero guard fires). When invalid
            // we keep `tvt` null so the UI can show "—" instead of a
            // misleading "$0.0000".
            const tvtValid = live.tvtValid === true;
            setCurrent({
                circulatingSupply: BigInt(live.circulatingSupply || "0"),
                totalSupply: BigInt(live.totalSupply || "0"),
                tvt: tvtValid && live.treasuryValuePerToken != null
                    ? BigInt(live.treasuryValuePerToken)
                    : null,
                tvtValid,
                // Live GIL market price (USDC-per-GIL × 1e8). null when the
                // pool is unseeded / spotPrice() reverted → UI shows "—".
                spotPrice: live.spotPrice != null ? BigInt(live.spotPrice) : null,
                totalAllocated: BigInt(live.totalAllocated || "0"),
                observedAt: live.observedAt || null
            });
            setError(null);
        } catch (err) {
            setError(err?.message || "Failed to fetch live TVT");
            if (!background) setCurrent(null);
        }
        try {
            const snapshots = await fetchTvtHistory({ limit: 90 });
            const items = (snapshots?.items || []).map((s) => ({
                timestamp: new Date(s.observedAt).getTime(),
                tvt: BigInt(s.treasuryValuePerToken || "0"),
                supply: BigInt(s.circulatingSupply || "0"),
                allocated: BigInt(s.totalAllocated || "0")
            }));
            setHistory(items);
        } catch {
            // History is best-effort — `current` is enough for live UI.
        }
        if (!background) setLoading(false);
    }, []);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(() => refresh({ background: true }), pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { current, history, isLoading, error, refresh };
}
